import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotStep, setForgotStep] = useState(1); // 1=enter email, 2=enter code+password
  const [forgotMsg, setForgotMsg] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, rememberMe);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setForgotMsg('');
    setLoading(true);
    try {
      if (forgotStep === 1) {
        await api.post('/auth/forgot-password', { email });
        setForgotMsg('A 6-digit reset code has been sent to your email.');
        setForgotStep(2);
      } else {
        await api.post('/auth/reset-password', { email, code: resetCode, new_password: newPassword });
        setForgotMsg('Password reset successful! You can now login.');
        setTimeout(() => { setForgotMode(false); setForgotStep(1); setForgotMsg(''); setResetCode(''); setNewPassword(''); }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B5486] relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/20 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full translate-x-1/3 translate-y-1/3" />
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="relative z-10 text-center px-12 max-w-lg">
          <div className="w-24 h-24 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-8 p-3">
            <img src="/logo.png" alt="Pace Infocom" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-4xl font-semibold text-white font-heading tracking-tight mb-4">PaceOps</h1>
          <p className="text-lg text-white/80 mb-2">Operations Simplified</p>
          <div className="w-16 h-0.5 bg-white/40 mx-auto my-6" />
          <p className="text-sm text-white/60 leading-relaxed">
            Your unified platform for sales pipeline, project management, BOQ, DWR, HR, AMC, and financial analytics.
          </p>
        </div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center px-6 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-10">
            <div className="w-14 h-14 rounded-xl bg-[#1B5486] flex items-center justify-center mx-auto mb-4 p-2">
              <img src="/logo.png" alt="Pace Infocom" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900 font-heading">PaceOps</h1>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-8">
            {!forgotMode ? (
              <>
                <h2 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight mb-1">Welcome back</h2>
                <p className="text-sm text-slate-500 mb-8">Sign in to access your PaceOps dashboard</p>

                {error && (
                  <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="login-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                    <input
                      type="email"
                      data-testid="login-email-input"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@paceinfocom.com"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        data-testid="login-password-input"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none transition-all pr-10"
                        required
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input type="checkbox" id="remember-me" data-testid="remember-me-checkbox"
                        checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                        className="w-4 h-4 text-[#1B5486] border-slate-300 rounded focus:ring-[#1B5486]" />
                      <label htmlFor="remember-me" className="ml-2 text-sm text-slate-600">Remember me</label>
                    </div>
                    <button type="button" onClick={() => { setForgotMode(true); setError(''); }} data-testid="forgot-password-link"
                      className="text-sm text-[#1B5486] hover:underline">Forgot password?</button>
                  </div>
                  <button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={loading}
                    className="w-full bg-[#1B5486] text-white py-2.5 rounded-md text-sm font-medium hover:bg-[#144066] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in...</> : 'Sign In'}
                  </button>
                </form>
              </>
            ) : (
              <>
                <button onClick={() => { setForgotMode(false); setForgotStep(1); setError(''); setForgotMsg(''); }}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
                  <ArrowLeft size={14} /> Back to login
                </button>
                <h2 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight mb-1">Reset Password</h2>
                <p className="text-sm text-slate-500 mb-6">
                  {forgotStep === 1 ? 'Enter your email to receive a reset code' : 'Enter the code sent to your email'}
                </p>

                {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>}
                {forgotMsg && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">{forgotMsg}</div>}

                <form onSubmit={handleForgotSubmit} className="space-y-4">
                  {forgotStep === 1 && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                        placeholder="you@paceinfocom.com"
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none" />
                    </div>
                  )}
                  {forgotStep === 2 && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">6-Digit Code</label>
                        <input type="text" value={resetCode} onChange={e => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required
                          placeholder="123456" maxLength={6}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm text-center text-lg tracking-[0.5em] font-mono focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                          placeholder="Minimum 8 characters" minLength={8}
                          className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 focus:border-[#1B5486] outline-none" />
                      </div>
                    </>
                  )}
                  <button type="submit" disabled={loading}
                    className="w-full bg-[#1B5486] text-white py-2.5 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                    {forgotStep === 1 ? 'Send Reset Code' : 'Reset Password'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-center text-xs text-slate-400 mt-6">
            Pace Infocom | PaceOps v3.0 | Avas Vikas, Rudrapur, Uttarakhand
          </p>
        </div>
      </div>
    </div>
  );
}
